import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Info } from "lucide-react";
import PilotBanner from "@/modules/racket_lab_admin/PilotBanner";
import RLNavSidebar from "@/modules/racket_lab_admin/RLNavSidebar";
import AdminApiTable from "@/modules/racket_lab_admin/AdminApiTable";
import TicketCreateDialog from "@/modules/racket_lab_admin/TicketCreateDialog";
import useAdminApi from "@/hooks/useAdminApi";
import { useRacketLabAdminT } from "@/i18n/racketLabAdminI18n";
import { TICKET_STATUS_LABEL_KEY, labelKeyFor } from "@/i18n/racketLabMaps";

/**
 * RLAdminTickets — Sub-sprint A list + SS-2 (Feb 2026) "New ticket" flow.
 *
 * Driven by GET /api/admin/racket-lab/tickets. SS-2 adds:
 *  • "Nuevo ticket" button (admin/staff/shop_manager via existing gate)
 *  • TicketCreateDialog wired to POST /api/admin/racket-lab/tickets
 *  • Inline QA health-check banner ("Cómo lo verás como QA tester")
 *
 * `data-testid` and i18n keys preserved verbatim where they existed.
 */
const STATUS_STYLES = {
  draft: "bg-slate-100 text-slate-700",
  checked_in: "bg-blue-100 text-blue-800",
  assigned: "bg-indigo-100 text-indigo-800",
  in_progress: "bg-amber-100 text-amber-800",
  pending_customer_approval: "bg-yellow-100 text-yellow-900",
  ready_for_pickup: "bg-emerald-100 text-emerald-800",
  closed: "bg-slate-200 text-slate-600",
  cancelled: "bg-rose-100 text-rose-800",
  deleted: "bg-slate-300 text-slate-500 line-through",
  // Legacy R3 buckets (kept for backwards-compat)
  pickup: "bg-sky-100 text-sky-800",
  diagnosis: "bg-purple-100 text-purple-800",
};

const StatusBadge = ({ status, t }) => {
  const cls = STATUS_STYLES[status] || "bg-slate-100 text-slate-700";
  return (
    <span
      data-testid={`rl-ticket-status-${status || "unknown"}`}
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${cls}`}
    >
      {t(labelKeyFor(TICKET_STATUS_LABEL_KEY, status))}
    </span>
  );
};

const QaHealthcheckBanner = ({ t, onOpen, dataTestId }) => (
  <details
    data-testid={dataTestId}
    className="rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-900"
  >
    <summary className="cursor-pointer font-medium flex items-center gap-2">
      <Info className="h-3.5 w-3.5" />
      {t("tickets.qa.title")}
    </summary>
    <ol className="mt-2 list-decimal space-y-1 pl-5">
      <li>{t("tickets.qa.step_1")}</li>
      <li>{t("tickets.qa.step_2")}</li>
      <li>{t("tickets.qa.step_3")}</li>
      <li>{t("tickets.qa.step_4")}</li>
      <li>{t("tickets.qa.step_5")}</li>
      <li>{t("tickets.qa.step_6")}</li>
      <li>{t("tickets.qa.step_7")}</li>
    </ol>
    <p className="mt-2 text-[11px]">
      <strong>{t("tickets.qa.pass_label")}:</strong> {t("tickets.qa.pass_criteria")}
    </p>
    <p className="mt-1 text-[11px]">
      <strong>{t("tickets.qa.fail_label")}:</strong> {t("tickets.qa.fail_criteria")}
    </p>
    <button
      type="button"
      data-testid="rl-ticket-qa-open-new"
      onClick={onOpen}
      className="mt-2 rounded-md bg-sky-600 px-3 py-1 text-[11px] font-medium text-white hover:bg-sky-700"
    >
      {t("tickets.qa.cta_open_new")}
    </button>
  </details>
);

const RLAdminTickets = () => {
  const t = useRacketLabAdminT();
  const navigate = useNavigate();
  const { data, loading, error, reload } = useAdminApi("/admin/racket-lab/tickets");
  const [dialogOpen, setDialogOpen] = useState(false);

  const rows =
    Array.isArray(data) ? data : data?.items || data?.tickets || [];

  const columns = [
    {
      key: "short_code",
      label: t("tickets.columns.code"),
      render: (row) => (
        <code data-testid={`rl-ticket-code-${row.id}`} className="text-xs font-mono">
          {row.short_code || row.id?.slice(0, 8) || "—"}
        </code>
      ),
    },
    {
      key: "status",
      label: t("tickets.columns.status"),
      render: (row) => (
        <span className="inline-flex items-center gap-1">
          <StatusBadge status={row.status} t={t} />
          {row.is_qa_test ? (
            <span
              data-testid={`rl-ticket-qa-badge-${row.id}`}
              className="rounded bg-purple-100 px-1 py-0.5 text-[9px] font-medium text-purple-700"
            >
              {t("tickets.qa_badge")}
            </span>
          ) : null}
        </span>
      ),
    },
    {
      key: "shop_id",
      label: t("tickets.columns.shop"),
      render: (row) => (
        <code className="text-[10px] text-slate-500">
          {(row.racket_lab_shop_id || row.shop_id)?.slice(0, 20) || "—"}
        </code>
      ),
    },
    {
      key: "customer",
      label: t("tickets.columns.customer"),
      render: (row) =>
        row.customer_reference_label ||
        row.customer_full_name ||
        row.owner_full_name ||
        (row.customer_user_id ? row.customer_user_id.slice(0, 10) : "—"),
    },
    {
      key: "created_at",
      label: t("tickets.columns.created"),
      render: (row) => (
        <span className="text-[10px] text-slate-500">
          {row.created_at?.slice(0, 19).replace("T", " ") || "—"}
        </span>
      ),
    },
  ];

  const onCreated = (ticket) => {
    setDialogOpen(false);
    reload?.();
    if (ticket?.id) {
      navigate(`/admin/racket-lab/tickets/${ticket.id}`);
    }
  };

  return (
    <div data-testid="rl-admin-tickets-page" className="flex flex-col gap-4">
      <PilotBanner />
      <RLNavSidebar />

      <header className="flex items-baseline justify-between">
        <h1 className="text-xl font-semibold text-slate-900">
          {t("tickets.title")}
        </h1>
        <div className="flex items-center gap-3">
          <p className="text-xs text-slate-500">
            {t(rows.length === 1 ? "tickets.counter" : "tickets.counter_plural", { count: rows.length })}
          </p>
          <button
            type="button"
            data-testid="rl-ticket-new-button"
            onClick={() => setDialogOpen(true)}
            className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-700"
          >
            <Plus className="h-3.5 w-3.5" />
            {t("tickets.new_button")}
          </button>
        </div>
      </header>

      <QaHealthcheckBanner
        t={t}
        onOpen={() => setDialogOpen(true)}
        dataTestId="rl-tickets-qa-healthcheck"
      />

      <AdminApiTable
        columns={columns}
        rows={rows}
        loading={loading}
        error={error}
        emptyLabel={t("tickets.empty")}
        onRowClick={(row) =>
          navigate(`/admin/racket-lab/tickets/${row.id}`)
        }
        dataTestId="rl-tickets-table"
      />

      <TicketCreateDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onCreated={onCreated}
      />
    </div>
  );
};

export default RLAdminTickets;
